// Composer Studio — 작성 탭 "위젯 자동 배치" 순수 연산 (P1 + 격자 표 미러 P2·P4)
//
// 작성 페이지 위젯 트리(composerWidgetTree)를 body 구조에서 **결정적으로**(AI 없이) 스캐폴드하는
// 툴바 도구. form-import 초안뿐 아니라 손저작 템플릿에도 쓰는 범용 도구다.
//  ① fillUnplacedWidgets: 비파괴 — 현재 트리는 그대로 두고 미배치 위젯만 소속 표 그룹에 삽입.
//  ② rebuildWidgetTreeFromBody: 파괴 — body를 걸어 트리를 새로 생성(호출부가 확인 모달 경유).
//
// 그룹핑 규칙은 서버 form-import 변환기(form-import.transformer.ts의 groupDrafts·pendingHeading)와
// 동형: heading(볼드 텍스트 노드)은 바로 아래 첫 표 하나만 제목짓는 소비형, 그 외 '표 N',
// 위젯 순서는 행→열 읽기 순서. 파생 대상 판정은 rc 정본 술어(createWriteFormBindingFilter)를
// 재사용한다(자체 재정의 금지 — 반복영역 밴드 셀/repeat 서브트리/compute 입력 자동 제외).
//
// 각 연산은 한 번의 반환으로 묶여 호출부가 `apply((t) => op(t, pool).next)` 단일 producer
// (=undo 1스텝)로 쓴다(formImportOps 관례 미러). pool은 호출부가 전달하는 파생 풀
// (deriveComposerWidgets 결과 — EditorPage 작성 탭 "미배치" 풀과 같은 소스).
// 결정: docs/decisions/2026-07-27-form-import-automation-boundary.md,
// 줄기: docs/work-streams/done/write-form-auto-layout.md (2026-10-04 완료·배포로 done 이관)
// 사용자 문서: docs/manual/composer/how-to/write-form-auto-layout.md
import type {
  ComposerNode,
  ComposerWidget,
  DataSourceDef,
  FormTableColumn,
  FormTableFixedCell,
  FormTableRow,
  FormTableWidget,
  GroupWidget,
  InputWidget,
  RepeatWidget,
  ReportTemplate,
  TableCell,
  TableNode,
  TextNode,
} from '@safetyhub/shared';
import {
  createRepeatWidgetForDataSource,
  createWriteFormBindingFilter,
  isEditableListDataSource,
  normalizeFormTable,
  syncComposerWidgets,
} from '@safetyhub/report-composer';

// ============================================================
// body 스캔 — 표 섹션(제목·읽기 순서 엔트리) + 키 소속 인덱스
// ============================================================

/** 표 1개가 만드는 작성 섹션의 구성원 — field=파생 대상 bindingKey / repeat=반복영역 over(데이터셋 key). */
interface SectionEntry {
  kind: 'field' | 'repeat';
  key: string;
  row: number;
  col: number;
}

/** 표 1개 → 작성 그룹 재료(제목 + 읽기 순서 엔트리). 입력 없는 정적 표는 섹션을 만들지 않는다. */
interface TableSection {
  tableId: string;
  title: string;
  entries: SectionEntry[];
  /**
   * 원본 표 노드 — 격자 블록 미러(P2)가 엔트리엔 없는 재료(텍스트 셀·병합·colCount·repeatRegions)를
   * 읽는다. 텍스트 셀을 entries에 따로 수집하지 않는 이유: 병합 점유맵이 필요해 어차피 셀 전수를
   * 다시 걸어야 하고, tableId로 body에서 표를 되찾는 하강 로직이 scanBody와 중복된다.
   */
  table: TableNode;
}

interface BodyScan {
  sections: TableSection[];
  /** 작성폼 파생 대상 bindingKey → 소유 표 노드 id(첫 등장 우선 — 파생 풀 dedup 규약과 정합). */
  keyToTable: Map<string, string>;
  /** repeatRegion.over(데이터셋 key) → 표 노드 id(첫 등장 우선). */
  overToTable: Map<string, string>;
}

/**
 * heading 판정 — 볼드(fontWeight>=600) 비어있지 않은 텍스트 노드.
 * 서버 form-import 변환기가 heading 블록을 fontWeight 700 TextNode로 내리는 것의 역판정이며,
 * 손저작 템플릿에서도 "굵은 제목 텍스트"가 섹션 제목이라는 같은 관례를 따른다.
 */
function isHeadingText(node: ComposerNode): node is TextNode {
  return (
    node.kind === 'text' &&
    typeof node.text === 'string' &&
    node.text.trim().length > 0 &&
    (node.style?.fontWeight ?? 0) >= 600
  );
}

/**
 * body를 한 번 걸어 표 섹션 목록과 키 소속 인덱스를 만든다.
 *
 * - heading(볼드 텍스트)은 pendingHeading으로 들고 있다가 **바로 아래 첫 유효 표**가 소비한다
 *   (한 제목 아래 표가 여럿이면 두 번째부터 '표 N' — 서버 변환기 동형).
 * - '표 N'의 N은 순회 중 만난 표의 1-based 순번(섹션 미생성 표도 순번은 차지 — 변환기의
 *   tableCount와 동형).
 * - 셀 자체 바인딩 + 셀 content 노드 바인딩(체크박스 등)을 그 셀 좌표 소속으로 수집하고,
 *   파생 대상 여부는 rc 정본 술어로 판정한다(반복영역 밴드/repeat 서브트리 자동 제외).
 * - repeat 노드(body 반복)는 template 서브트리가 반복 행 데이터라 하강하지 않는다.
 */
function scanBody(template: ReportTemplate): BodyScan {
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  const sections: TableSection[] = [];
  const keyToTable = new Map<string, string>();
  const overToTable = new Map<string, string>();
  let pendingHeading: string | undefined;
  let tableCount = 0;

  const visitTable = (table: TableNode): void => {
    tableCount += 1;
    const entries: SectionEntry[] = [];
    const addField = (bindingKey: string, row: number, col: number): void => {
      if (keyToTable.has(bindingKey)) return; // 같은 키 중복 배치 방지 — 첫 표 소속(전역 첫 등장 우선)
      keyToTable.set(bindingKey, table.id);
      entries.push({ kind: 'field', key: bindingKey, row, col });
    };
    // 위젯 순서는 문서를 읽는 순서(행→열) — cells 배열 순서가 아니라 좌표 기준.
    const cellsInReadingOrder = [...table.cells].sort((a, b) => a.row - b.row || a.col - b.col);
    for (const cell of cellsInReadingOrder) {
      if (
        cell.binding &&
        isWriteFormBinding({
          cell: { tableId: table.id, row: cell.row, col: cell.col },
          source: 'cell',
        })
      ) {
        addField(cell.binding.bindingKey, cell.row, cell.col);
      }
      if (cell.content) {
        visitNodes(cell.content, { addField, row: cell.row, col: cell.col });
      }
    }
    // 반복영역이 적용된 표 — 밴드 셀 필드는 위 정본 술어가 flat에서 제외하고, 대신 그 over
    // 데이터셋 위젯이 밴드 시작 행 자리(읽기 순서)에 들어간다.
    for (const region of [...(table.repeatRegions ?? [])].sort((a, b) => a.rowStart - b.rowStart)) {
      if (overToTable.has(region.over)) continue;
      overToTable.set(region.over, table.id);
      entries.push({
        kind: 'repeat',
        key: region.over,
        row: Math.min(region.rowStart, region.rowEnd),
        col: -1,
      });
    }
    entries.sort((a, b) => a.row - b.row || a.col - b.col);
    // 구성원 없는 정적 표는 섹션을 만들지 않는다(빈 섹션 방지 — 변환기 동형). heading도 미소비.
    if (entries.length === 0) return;
    sections.push({
      tableId: table.id,
      title: pendingHeading ?? `표 ${tableCount}`,
      entries,
      table,
    });
    pendingHeading = undefined;
  };

  interface CellContext {
    addField: (bindingKey: string, row: number, col: number) => void;
    row: number;
    col: number;
  }
  const visitNodes = (nodes: ComposerNode[], cellCtx?: CellContext): void => {
    for (const node of nodes) {
      // heading은 흐름(표 밖) 레벨에서만 — 셀 content 안 텍스트는 섹션 제목이 아니다.
      if (!cellCtx && isHeadingText(node)) pendingHeading = node.text;
      // 셀 content 안 노드 바인딩(체크박스·선택지 그룹 등)은 그 셀 좌표 소속 필드.
      if (
        cellCtx &&
        node.kind !== 'table' &&
        node.binding &&
        isWriteFormBinding({ nodeId: node.id, node, source: 'node' })
      ) {
        cellCtx.addField(node.binding.bindingKey, cellCtx.row, cellCtx.col);
      }
      switch (node.kind) {
        case 'group':
        case 'stack':
          visitNodes(node.children, cellCtx);
          break;
        case 'branch':
          for (const slot of node.branches) visitNodes(slot.children, cellCtx);
          break;
        case 'table':
          visitTable(node);
          break;
        // repeat: template 서브트리는 반복 행 데이터(flat 파생 제외 — 정본 술어와 동일 판단) — 미하강.
        default:
          break;
      }
    }
  };
  visitNodes(template.body);
  return { sections, keyToTable, overToTable };
}

// ============================================================
// 격자 블록 탐지 — body 표의 직사각 입력 격자 (P2: 작성 탭 formTable 미러 재료)
// ============================================================
//
// ⚠️ **정답지(dev `safety-management-status-report` body `table-7` 우측 격자)와 의도적으로 다른
//    세 가지 — 한자리에 모아 둔다.** 흩어 적으면 다음 사람이 그중 하나를 「정답지와 다르니 버그」로
//    읽고 되돌린다. 손저작본은 **운영자가 손으로 다듬은 결과**라 그 자체가 규약이 아니다.
//
//  ① **행 머리말 칸을 싣는다.** 손저작본의 데이터 행 첫 칸은 `text:''`(빈 칸)인데 body에는
//     `전년도`·`당해년도`가 있다. 운영자가 비워 둔 것이고, 기계 미러는 body에 있는 그 라벨을
//     싣는 것이 옳다(비우면 어느 행이 어느 연도인지 작성 화면에서 사라진다).
//  ② **열 폭을 지정하지 않는다**(auto 균등). 손저작본은 7열 전부 `11.1111%`(=100/9)로 합이
//     77.8%다 — 9열 body에서 복제한 흔적이고, 블록 밖 열까지 포함한 분배를 잘라 온 값이다.
//     ⚠️ 손저작본의 **열 수 자체도 폭 조절의 산물**일 수 있다(다른 양식에 body 2열 → formTable
//     3열 + colSpan 2 + 26/16/58% 배분 사례가 있다). 그래서 열 수 일치는 수용 기준이 아니고
//     기준은 **셀 내용과 배치**다.
//  ③ **범위를 통째로 덮는 최상단 헤더 셀을 표 «제목»으로 올린다.** 안 올리면 leaf 평탄화에 덮여
//     그 문구가 트리에서 통째로 사라진다. 또 「한 body 표 → 한 group → formTable 여럿」이 이미
//     확립된 관례라(손저작 양식도 근로자현황·재해발생현황 두 표가 같은 group '사업장 개요' 안에
//     있다) group 제목은 heading 소비형이고 **블록 구분은 표 제목이 맡는 것이 맞다**.
//
// 그리고 손저작본이 다듬은 **문구**(`우수포인트` → `우수 포인트`처럼 공백을 넣은 것)도 따르지
// 않는다 — 기계는 body 텍스트를 그대로 쓴다.

/**
 * formTable로 미러할 수 있는 직사각 입력 구간 하나.
 *
 * 한 body 표 안에 격자가 **여럿 나란히** 있을 수 있어(실제 양식: 좌측 근로자현황 2열 + 우측
 * 재해발생현황 7열이 한 표) 표 단위가 아니라 블록 단위로 잡는다.
 */
interface GridBlock {
  colStart: number;
  colEnd: number;
  /** 데이터 행(오름차순·연속) — 각 행이 colStart~colEnd를 병합·빈틈 없이 채운다. */
  dataRows: number[];
  /** 열 → leaf 헤더 텍스트(다층 헤더를 가장 깊은 라벨로 평탄화). 비어 있으면 열 라벨 없음. */
  headerByCol: Map<number, string>;
  /** 범위 전체를 덮는 최상단 헤더 셀 텍스트 — 표 위젯 제목(없으면 undefined). */
  title?: string;
}

/**
 * (row,col) → 그 좌표를 점유하는 셀. 병합에 덮인 좌표는 top-left **소유 셀**을 가리킨다.
 * ⚠️ body `TableCell`의 병합 키는 소문자 `rowspan/colspan`이다(작성폼 `FormTableFixedCell`의
 * `rowSpan/colSpan`과 다른 이름 — 혼용하면 병합이 조용히 무시된다).
 */
function buildCellOccupancy(table: TableNode): Map<string, TableCell> {
  const occ = new Map<string, TableCell>();
  for (const cell of table.cells) {
    const rs = Math.max(1, cell.rowspan ?? 1);
    const cs = Math.max(1, cell.colspan ?? 1);
    for (let r = cell.row; r < cell.row + rs; r += 1) {
      for (let c = cell.col; c < cell.col + cs; c += 1) {
        const k = `${r}:${c}`;
        if (!occ.has(k)) occ.set(k, cell);
      }
    }
  }
  return occ;
}

/**
 * 표에서 격자 블록을 찾는다 — **구조만 보는 결정적 판정**(의미 추론 없음.
 * 결정: docs/decisions/2026-07-27-form-import-automation-boundary.md).
 *
 * ① **값 열**(입력 엔트리 또는 compute 셀을 소유한 열)의 **연속 런**이 블록 후보.
 *    ⚠️ compute를 값으로 세지 않으면 계산 열에서 런이 끊겨 한 격자가 둘로 쪼개진다
 *    (실제 양식의 「재해자수 계」가 compute 열이라 그 자리에서 끊긴다).
 * ② 좌측으로 **라벨 열**을 흡수 — 값이 없고, 데이터 행 전부에 병합 없는 자기 셀이 있고,
 *    그중 텍스트가 하나라도 있는 열(행 머리말). 값 열에 닿으면 멈추므로 블록 범위는 겹치지 않는다.
 * ③ **데이터 행** = 런 범위에서 값 셀을 소유한 행. 연속이어야 하고(⚠️ **범위 안에서 아무것도
 *    소유하지 않는 중간 행은 투명하게 넘긴다** — 빈 좌표/세로 병합에 덮인 좌표. P4),
 *    범위 전체가 **가로 병합·빈틈 없는 자기 셀**이어야 한다(직사각 — `isGridOwn`).
 *    아니면 블록을 만들지 않는다 — 호출부가 종전 flat group으로 폴백.
 * ④ 게이트: **데이터 행 ≥2**(한 행뿐이면 반복 레코드가 아니다) · **repeatRegion 미교차**(반복
 *    행은 다른 축 — formTable repeat 행 매핑은 범위 밖) · 값 열이 **1개**면(「라벨│값」 세로
 *    격자) **「이름 붙은 표(title ∨ headerByCol)」 ∧ 「머리말 열 흡수(colStart < run.min)」**
 *    일 때만(P4 — 둘 다 필요하고 어느 하나도 충분하지 않다. 근거·반례는 그 자리 주석).
 *    종전엔 1값열을 통째로 배제했는데, 그래서 실데이터 근로자현황 두 입력이 flat으로 풀려
 *    **라벨이 완전히 동일**해졌다. 전치는 여전히 의미 판단이라 범위 밖 — 행·열은 body 그대로.
 * ⑤ **헤더** = 데이터 행 위로 「범위에 값 셀 없음 ∧ 텍스트 있음」인 연속 행들. leaf 평탄화는
 *    위→아래 last-write-wins로 얻는다 — 병합 owner의 텍스트가 덮인 좌표에도 깔려 있어
 *    깊은 행의 leaf 라벨이 상위 묶음 라벨을 덮어쓴다(다층 헤더 → 1행).
 */
function detectGridBlocks(table: TableNode, keysByCell: Map<string, string[]>): GridBlock[] {
  const occ = buildCellOccupancy(table);
  const at = (row: number, col: number): TableCell | undefined => occ.get(`${row}:${col}`);
  const own = (row: number, col: number): TableCell | undefined => {
    const cell = at(row, col);
    return cell && cell.row === row && cell.col === col ? cell : undefined;
  };
  /**
   * 격자 한 칸으로 쓸 수 있는 자리 — 그 좌표를 **소유**하고(top-left 동일) **가로 병합이 없다**.
   *
   * ⚠️ `rowspan`은 **보지 않는다**(P4). 세로 병합은 body 레이아웃의 산물이고, 덮인 행은 아래
   * 연속성 검사가 「투명」으로 넘겨 미러 표에 아예 들어가지 않으므로 한 칸으로 접힌다.
   * 그래도 **덮인 행이 «데이터 행»이면 거부된다** — 그 행은 그 열의 소유 셀을 잃어 이 같은
   * 검사에 걸린다. 즉 한 body 셀이 미러 두 행에 중복 배치되는 사고를 이 한 술어가 막는다
   * (그래서 rowspan을 따로 검사할 필요가 없다 — 검사했으면 실데이터 근로자현황이 떨어진다).
   * 반면 `colspan>1`은 열 정렬을 깨므로 거부한다(실제 양식의 병합 입력 칸이 그 형상).
   */
  const isGridOwn = (row: number, col: number): boolean => {
    const cell = own(row, col);
    return !!cell && (cell.colspan ?? 1) === 1;
  };
  /** 그 행이 범위 안에서 **소유하는** 셀이 하나도 없다(빈 좌표 / 세로 병합에 덮인 좌표). */
  const ownsNothingIn = (row: number, min: number, max: number): boolean => {
    for (let c = min; c <= max; c += 1) if (own(row, c)) return false;
    return true;
  };
  const hasText = (cell: TableCell | undefined): boolean => (cell?.text ?? '').trim() !== '';
  const keysOf = (cell: TableCell | undefined): string[] =>
    cell ? (keysByCell.get(`${cell.row}:${cell.col}`) ?? []) : [];
  const isValue = (cell: TableCell | undefined): boolean =>
    !!cell && (keysOf(cell).length > 0 || !!cell.compute);

  // 좌표 상한은 선언값과 실제 셀 중 큰 쪽(선언이 낡은 저장본 방어).
  const colCount = table.cells.reduce(
    (m, c) => Math.max(m, c.col + Math.max(1, c.colspan ?? 1)),
    table.colCount ?? 0,
  );
  const rowCount = table.cells.reduce(
    (m, c) => Math.max(m, c.row + Math.max(1, c.rowspan ?? 1)),
    table.rowCount ?? 0,
  );

  // ① 값 열의 연속 런
  const valueCols = new Set<number>();
  for (const cell of table.cells) if (isValue(cell)) valueCols.add(cell.col);
  const runs: { min: number; max: number }[] = [];
  for (let c = 0; c < colCount; c += 1) {
    if (!valueCols.has(c)) continue;
    const last = runs[runs.length - 1];
    if (last && last.max === c - 1) last.max = c;
    else runs.push({ min: c, max: c });
  }

  const regions = table.repeatRegions ?? [];
  const blocks: GridBlock[] = [];
  for (const run of runs) {
    // ③ 데이터 행
    const dataRows: number[] = [];
    for (let r = 0; r < rowCount; r += 1) {
      let found = false;
      for (let c = run.min; c <= run.max && !found; c += 1) found = isValue(own(r, c));
      if (found) dataRows.push(r);
    }
    if (dataRows.length < 2) continue; // ④ 반복 레코드가 아니다
    // 불연속 — 단 **범위 안에서 아무것도 소유하지 않는 행은 투명하게 넘긴다**(P4).
    // 그런 행은 ⓐ 셀이 아예 없는 빈 좌표이거나 ⓑ 세로 병합에 덮인 좌표이고, 둘 다 미러 표에
    // 실을 내용이 없다. **한 술어로 두 형상을 덮는 것이 요점이다** — 「빈 행만 건너뛴다」로 쓰면
    // 실데이터(근로자현황의 rowspan=2 덮인 r2)가 조용히 남고, 「덮인 행만」으로 쓰면 빈 좌표
    // 형상이 남는다. 반대로 **텍스트·배너 셀을 소유한** 중간 행은 투명이 아니라 불연속이다
    // (뭉개면 그 문구가 트리에서 사라진다) — 그래서 `isGridOwn`이 아니라 `own` 기준이다.
    // ⚠️ **이 판정은 「빈 좌표」와 「병합에 덮인 좌표」를 구분하지 않는다 — 그리고 그 축의 탐지기는
    // 없다.** 더 좁은 해석(「범위의 모든 좌표가 **데이터 행의 소유 셀에 덮일** 때만 투명」 = 빈
    // 좌표는 투명 아님)을 2026-10-04 dev 24 템플릿 38블록에 나란히 돌린 결과 **차이가 0건**이라
    // 좁히지 않았다(발동하지 않는 가드를 만드는 셈이다). 둘이 갈리는 데이터 — **한 열에서 빈 행으로
    // 분리된 별개 격자 둘** — 이 나타나면 그때 좁혀야 한다. 그 전까지 이 선택을 재는 spec은 없다.
    // ⚠️ **경계**: 이 검사는 **값 열 런 범위만** 본다. 좌측 라벨 열 흡수(②)는 그 뒤에 일어나므로,
    // 중간 행이 **흡수될 라벨 열에만** 걸쳐 있으면(예: col0 colspan 2 배너) 여전히 투명으로 넘어가
    // 미러에서 빠진다. flat 폴백도 텍스트 셀을 못 싣기에 손실 차이가 없어 의도적으로 두었다 —
    // 넓히려면 흡수가 끝난 범위로 한 번 더 재야 하므로 별 작업이다(발췌 하네스로 실행 확인).
    const dataRowSet = new Set(dataRows);
    let contiguous = true;
    for (let r = dataRows[0] + 1; r < dataRows[dataRows.length - 1] && contiguous; r += 1) {
      if (dataRowSet.has(r) || ownsNothingIn(r, run.min, run.max)) continue;
      contiguous = false;
    }
    if (!contiguous) continue;
    // 직사각 검사 + 한 셀이 키 여럿을 품는 복합 셀 배제(셀 하나엔 위젯 하나만 담긴다).
    let rectangular = true;
    for (const r of dataRows) {
      for (let c = run.min; c <= run.max; c += 1) {
        if (!isGridOwn(r, c) || keysOf(own(r, c)).length > 1) {
          rectangular = false;
          break;
        }
      }
      if (!rectangular) break;
    }
    if (!rectangular) continue;

    // ② 좌측 라벨 열 흡수 — 전 데이터 행에 병합 없는 자기 셀이 있어야 직사각이 유지된다.
    let colStart = run.min;
    while (
      colStart > 0 &&
      !valueCols.has(colStart - 1) &&
      dataRows.every((r) => isGridOwn(r, colStart - 1)) &&
      dataRows.some((r) => hasText(own(r, colStart - 1)))
    ) {
      colStart -= 1;
    }
    const colEnd = run.max;

    // ⑤ 헤더 행 — 데이터 행 바로 위로 연속 수집(위를 향해).
    const headerRows: number[] = [];
    for (let r = dataRows[0] - 1; r >= 0; r -= 1) {
      let rowHasValue = false;
      let rowHasText = false;
      for (let c = colStart; c <= colEnd; c += 1) {
        const cell = at(r, c); // 덮인 좌표도 본다 — 위 셀이 이 행까지 내려오면 헤더가 아니다
        if (isValue(cell)) {
          rowHasValue = true;
          break;
        }
        if (hasText(cell)) rowHasText = true;
      }
      if (rowHasValue || !rowHasText) break;
      headerRows.push(r);
    }
    headerRows.reverse();

    // 범위를 통째로 덮는 최상단 헤더 셀은 열 라벨이 아니라 블록 이름 — 표 제목으로 올린다
    // (근거는 이 절 머리의 「의도적으로 다른 세 가지」 ③).
    let title: string | undefined;
    const topRow = headerRows[0];
    if (topRow !== undefined) {
      const spanning = own(topRow, colStart);
      if (
        spanning &&
        hasText(spanning) &&
        Math.max(1, spanning.colspan ?? 1) === colEnd - colStart + 1
      ) {
        title = spanning.text;
        headerRows.shift();
      }
    }

    // ④ repeatRegion 교차 — 반복 행은 다른 축이라 미러하지 않는다.
    const rowFrom = headerRows[0] ?? dataRows[0];
    const rowTo = dataRows[dataRows.length - 1];
    const crossesRepeat = regions.some(
      (rg) =>
        Math.min(rg.rowStart, rg.rowEnd) <= rowTo && Math.max(rg.rowStart, rg.rowEnd) >= rowFrom,
    );
    if (crossesRepeat) continue;

    const headerByCol = new Map<number, string>();
    for (const r of headerRows) {
      for (let c = colStart; c <= colEnd; c += 1) {
        const text = at(r, c)?.text;
        if (text !== undefined && text.trim() !== '') headerByCol.set(c, text);
      }
    }
    // ④ 값 열이 **1개**인 블록(「라벨│값」 세로 격자) — 조건 **둘을 AND**로 요구한다(P4).
    //
    //   ⓐ **이름 붙은 표**: `title`(범위를 덮는 최상단 헤더 승격) ∨ `headerByCol` 비어있지 않음.
    //   ⓑ **머리말 열이 흡수됐다**: `colStart < run.min`.
    //
    // ⭐ **1값열 거부 사유는 «둘»이고 각각 «다른 spec 케이스»가 소유한다 — 한쪽을 「중복」이라며
    //    지우지 말 것**(지우면 그 사유가 통째로 감시 밖이 된다):
    //      · **이름이 없다**(ⓐ 실패) → `[되돌림] 헤더·제목 없는 1값열 라벨:값 표는 미러하지 않는다`
    //        (`기 관 명│agencyName` — 폼 필드 목록)
    //      · **머리말이 없다**(ⓑ 실패) → `[되돌림] 머리말 열이 흡수되지 않으면 … 비고 열 형상`
    //        (`비  고` 열 — 헤더 셀 `rowspan=2`가 제목 승격 후에도 headerByCol에 남아 헤더 가드를
    //        비활성화하므로 ⓑ가 유일한 방어다)
    //
    // **두 조건의 «무게가 다르다» — 2026-10-04 dev 24 템플릿 전수 census(113 run 후보) 실측:**
    //  - ⓐ는 **살아 있는 게이트다 — 게이트에 도달한 29 run 중 23건을 홀로 막는다.**
    //    막는 것은 `si-header-table`·`ra-header-table`·`sma-appointment-table`·`dlr-header-table`·
    //    `tml-*-critical-table` 같은 **폼 필드 목록**이다(머리말은 있지만 헤더·제목이 없다).
    //    ⓑ만 요구하면 `기 관 명│agencyName`이 통과한다 — 그건 **머리말이 있다**.
    //  - ⓑ는 **오늘 0건을 막는다**(그 23건 중 ⓑ 단독 거부는 0 — ⓐ가 이미 다 잡는다).
    //    ⚠️ 그래도 남긴 것은 **형상이 도달 가능**하기 때문이다(프로브로 실측). 그 형상은 **두
    //    조건의 교집합**이라 좁다:
    //      · 값 열이 **leaf 헤더 라벨을 가졌다** → 아래 `formTableForBlock`의 헤더 가드가 **발동
    //        하지 않는다**. 전형적 기제는 헤더 셀 **`rowspan` ≥ 2**다 — 제목으로 승격되면
    //        `headerRows`에서 그 행만 빠지는데 **아래 행의 그 좌표를 같은 셀이 덮고 있어** 텍스트가
    //        다시 수집되어 `headerByCol`에 남는다(헤더가 두 행인 변종도 같은 결과).
    //      · **머리말 열 흡수가 실패한다** → 좌측 열이 아예 없거나(값 열이 col 0), 값 열이거나,
    //        데이터 행 전부에서 빈 텍스트인 경우.
    //    둘이 겹치면 **ⓑ가 유일한 방어**이고, 통과하면 **행 라벨이 아예 없는 1열 × N행 표**가 나온다
    //    (셀 렌더가 위젯 라벨을 억제하므로 P4가 고치려는 결함 그 자체다).
    //    ⚠️ 반면 값 열에 **헤더 라벨이 없으면** 헤더 가드가 ⓑ와 **이중으로** 막으므로 그 변종으로는
    //    **ⓑ를 재지 못한다**(ⓑ를 되돌려도 green이다 — 그 픽스처를 한 번 썼다가 프로브에서 드러났다).
    //    즉 **「0건」은 「일어날 수 없다」가 아니라 「오늘 데이터에 없다」**다. 임포트 초안이 머리말
    //    열 없는 **1열 비고 표**를 만들면 바로 그 형상이 된다.
    //    ⇒ **이 조건의 초록을 「그 형상이 막힌다」의 근거로 인용하지 말 것** — 오늘의 소유자는 ⓐ다.
    //    재는 유일한 자리는 spec `[되돌림] 머리말 열이 흡수되지 않으면 … 비고 열 형상`이고,
    //    거꾸로 **「0건이니 지우자」로도 쓰지 말 것**(지우면 그대로 통과한다).
    //
    // ⚠️ **그 census를 다시 돌릴 사람에게: `isValue`의 축은 셀 바인딩만이 아니다.** 셀 `content`
    // 노드의 바인딩도 **그 셀 좌표의 키**다(`scanBody`가 그렇게 수집한다 — 체크박스·선택지 그룹).
    // 그것을 빼고 세면 값 열이 쪼개져 **없는 1값열 run이 생긴다**: `sheet1/fi-4`의 `미흡`·`보통`·
    // `양호` 열(c3·c4·c5)은 셀 자체엔 바인딩이 없고 **content `checkbox` 노드**에 있다
    // (r2 기준 `managerEval1Poor`·`managerEval1Normal`·`managerEval1Good` — 한 줄로 검산된다).
    // 그래서 그것을 빼고 세면 `[6..6]`이
    // 독립 1값열로 보이지만 **실제로는 `[3..6]` 4값열 run의 일부**다(그 블록은 머리말 21개와
    // 헤더 5열을 갖춰 안전하다). 2026-10-04에 그 누락으로 이 자리의 근거가 한 번 틀렸다.
    //
    // ⚠️⚠️ **이 둘은 «근본 축»이 아니라 프록시다.** 근본은 **「flat 폴백이 라벨을 잃는가」**다 —
    // 근로자현황이 결함이었던 이유는 두 입력이 flat 라벨 폴백에서 **같은 라벨**(catalog 라벨)을
    // 받아서이고, 기관명/대표자는 각자 다른 라벨을 받아 멀쩡하다. 근본 축으로 바꾸려면 **블록 탐지
    // 시점에 flat 라벨을 계산할 수 있어야 하는데 지금은 못 한다**(라벨은 파생 풀이 들고 있고 이
    // 함수는 구조만 본다). 프록시를 근본으로 오인하면 전제가 바뀌는 날 조용히 틀리므로, 처방을
    // 바꾸지는 말되 **이름은 정직하게** 둔다.
    //
    // ⚠️ 전치(라벨을 열로 세우기)는 여전히 자동화 범위 밖이다(결정 2026-07-27) — 행·열은 body 그대로.
    // ⚠️ 손저작 라벨│값 formTable이 실재하는 형상 증거(`si-bp-detail`)가 있지만 **이 자동 미러는
    //    그보다 보수적이다** — 그 표들은 헤더 행이 없어(ⓐ 실패) 여기서 거부되고, 애초에 `repeat`
    //    안에 살아 재구성 경로로는 **원리적으로 재현 불가**다. 「P4가 그 형상을 재현한다」로 읽지 말 것.
    if (run.max === run.min && !((title !== undefined || headerByCol.size > 0) && colStart < run.min))
      continue;
    blocks.push({ colStart, colEnd, dataRows, headerByCol, title });
  }
  return blocks;
}

/**
 * 격자 블록 → 작성폼 표 위젯(formTable). 구조·내용만 미러한다(정렬·폰트 등 시각 축은
 * formTable 기본값 — body 시각을 끌어오면 두 축이 갈려 유지가 안 된다).
 *
 * - 열 라벨은 **구모델 `columns[].label`로 지어 `normalizeFormTable`에 통과**시킨다. 그러면
 *   헤더 고정 행 + 배경·폰트 박제가 그 함수 한 곳에서 나온다(스타일 하드코딩 금지 규약 —
 *   admin 「＋ 표 위젯」 생성부와 같은 경로). 통과 후 columns에는 label이 남지 않는다.
 * - 열 폭은 **미지정(auto 균등)** — 근거는 이 절 머리의 「의도적으로 다른 세 가지」 ②.
 * - 입력 셀은 **파생 풀의 위젯을 그대로** 담는다(형상·라벨 정본은 풀 — 재구성 금지. 라벨 폴백
 *   `catalog.label → binding.label → bindingKey`도 그래서 자동으로 유지된다).
 * - compute 셀은 body `CellCompute`를 그대로 옮긴다(같은 타입). 작성폼 compute는 **표시 전용**이라
 *   입력이 블록 밖에 있어 값이 안 잡히면 빈 칸으로 보일 뿐이고, 최종 보고서는 양식 탭이 계산한다.
 * - 풀 위젯이 하나도 없는 블록은 **미러하지 않는다**(null) — 입력 없는 정적 표를 작성 탭에
 *   올리는 것은 소득이 없다. 호출부가 종전 flat 경로로 폴백한다.
 */
function formTableForBlock(
  block: GridBlock,
  table: TableNode,
  keysByCell: Map<string, string[]>,
  poolByKey: Map<string, InputWidget>,
  widgetId: string,
): { widget: FormTableWidget; placedKeys: string[] } | null {
  const occ = buildCellOccupancy(table);
  const columnIdOf = (col: number): string => `${widgetId}-c${col}`;
  const columns: FormTableColumn[] = [];
  for (let c = block.colStart; c <= block.colEnd; c += 1) {
    const label = block.headerByCol.get(c);
    columns.push({ id: columnIdOf(c), ...(label ? { label } : {}) });
  }

  const placedKeys: string[] = [];
  const widgetCols = new Set<number>();
  /** 위젯을 담은 모든 행에 비어있지 않은 텍스트 칸(행 머리말)이 있나 — 아래 헤더 가드가 읽는다. */
  let everyWidgetRowHasText = true;
  const rows: FormTableRow[] = block.dataRows.map((r): FormTableRow => {
    const cells: FormTableFixedCell[] = [];
    for (let c = block.colStart; c <= block.colEnd; c += 1) {
      const columnId = columnIdOf(c);
      const cell = occ.get(`${r}:${c}`);
      const keys = cell ? (keysByCell.get(`${cell.row}:${cell.col}`) ?? []) : [];
      const widget = keys.length === 1 ? poolByKey.get(keys[0]) : undefined;
      if (widget) {
        placedKeys.push(keys[0]);
        widgetCols.add(c);
        cells.push({ columnId, widget });
      } else if (cell?.compute) {
        cells.push({ columnId, compute: cell.compute });
      } else {
        // 텍스트(행 머리말)·집계·파생 제외 바인딩 — 격자를 유지하려 빈 텍스트 셀로 둔다.
        cells.push({ columnId, text: cell?.text ?? '' });
      }
    }
    if (
      cells.some((c) => c.widget) &&
      !cells.some((c) => (c.text ?? '').trim() !== '')
    ) {
      everyWidgetRowHasText = false;
    }
    return { type: 'fixed', id: `${widgetId}-r${r}`, cells };
  });

  if (placedKeys.length === 0) return null;
  // ⚠️ **입력을 담는 열이 leaf 헤더 라벨을 못 가지면 미러하지 않는다**(P3 라벨 보존과 충돌 금지).
  // formTable 셀 렌더는 위젯의 `label`·`labelHint`를 **억제**하고 헤더가 그 역할을 한다
  // (rc renderCellWidget의 `label: undefined`). 그래서 헤더 없는 격자를 표로 올리면 한글 라벨이
  // 통째로 사라져 flat 폴백보다 나빠진다 — 임포트 초안의 「밀도 100% 격자」(텍스트 셀이 아예 없는
  // 2×3·5×7 등)가 정확히 그 형상이다. 라벨만 있는 행 머리말 열은 억제 대상이 아니라 제외.
  //
  // ⚠️ **단 「라벨│값」 세로 격자에서는 «행 머리말»이 그 역할을 대신한다**(P4 — 실데이터
  // 근로자현황은 열 헤더가 제목(`근로자현황`)으로 승격돼 leaf 헤더가 비어 있지만, 각 행의
  // `사무직`·`사무직외`가 그 입력을 식별한다). 그 완화는 **입력 열이 «하나»일 때만** 성립한다 —
  // 입력 열이 둘 이상이면 한 행의 머리말 하나가 여러 입력을 **유일하게 식별하지 못해** 라벨 없는
  // 위젯이 나란히 생긴다. 그래서 밀도 100% 격자(텍스트 셀 0개 · 입력 열 여럿)는 **양쪽 조건 다
  // 실패해 종전대로 거부된다** — 이 완화는 종전 거부 집합을 넓히기만 하고 뚫지 않는다.
  //
  // ⚠️ **`detectGridBlocks`의 1값열 게이트 ⓑ(머리말 열 흡수)와 «축이 다르다» — 소유자를 혼동하지
  // 말 것.** 게이트 ⓑ는 **「머리말 «열»이 블록에 있나」**를 묻고(흡수 조건이 `some(hasText)`라
  // **일부 행만 채워져 있어도** 통과한다), 여기 `everyWidgetRowHasText`는 **「위젯을 담은 «전 행»에
  // 머리말이 채워져 있나」**를 묻는다. 그래서 ⓑ를 통과한 블록도 여기서 거부될 수 있고, 그것이
  // 의도다 — ⓑ가 못 막는 「머리말 열은 있는데 어떤 행은 비어 있다」를 이쪽이 막는다.
  // ⚠️ **갈리는 선은 「값 열이 leaf 헤더 라벨을 가졌나」다**(프로브로 실측):
  //   · 값 열에 헤더 라벨이 **없다** → 이 가드가 발동해 ⓑ와 **이중으로** 막는다.
  //   · 값 열에 헤더 라벨이 **있다**(헤더 2단에서 위가 제목으로 승격된 경우) → 이 가드는 발동하지
  //     않고 **ⓑ가 유일한 방어**다. 그래서 둘 중 하나만 두면 그 형상이 통째로 새어 나간다.
  // 같은 것을 두 곳에서 재는 것이 아니므로 「중복」이라며 어느 한쪽을 지우지 말 것.
  // ⚠️ 이 완화 역시 **「flat이 라벨을 잃는가」의 프록시**다(위 게이트 주석의 같은 한계).
  if ([...widgetCols].some((c) => !block.headerByCol.has(c))) {
    if (widgetCols.size > 1 || !everyWidgetRowHasText) return null;
  }
  const widget = normalizeFormTable({
    kind: 'formTable',
    id: widgetId,
    ...(block.title ? { title: block.title } : {}),
    columns,
    rows,
  });
  return { widget, placedKeys };
}

// ============================================================
// id 발급 — 기존 트리와 충돌 없는 결정적 id
// ============================================================

/** 트리의 모든 위젯 id 수집(신규 id 충돌 회피용 — formTable 고정 셀 위젯 포함). */
function collectWidgetIds(tree: ComposerWidget[], into = new Set<string>()): Set<string> {
  for (const w of tree) {
    into.add(w.id);
    if (w.kind === 'group' || w.kind === 'repeat' || w.kind === 'modalTrigger') {
      collectWidgetIds(w.children, into);
    } else if (w.kind === 'branch') {
      for (const slot of w.branches) collectWidgetIds(slot.children, into);
    } else if (w.kind === 'formTable') {
      for (const row of w.rows) {
        if (row.type !== 'fixed') continue;
        for (const cell of row.cells) if (cell.widget) into.add(cell.widget.id);
      }
    }
  }
  return into;
}

/** base 그대로 쓰되 이미 있으면 `-2`, `-3` … 접미(결정적 — Date.now/random 금지 관례). */
function makeWidgetIdAllocator(taken: Set<string>): (base: string) => string {
  return (base: string): string => {
    let id = base;
    let n = 2;
    while (taken.has(id)) id = `${base}-${n++}`;
    taken.add(id);
    return id;
  };
}

/**
 * dataSourceRows 풀 위젯 → 반복 위젯 + 뼈대 children 대체 생성.
 * 편집형 조립 데이터소스의 신규 배치는 repeat으로 강제 단일화(handleWidgetPlace·2026-07-20 결정
 * 동형)하되, id는 결정적(`rw-<dsKey>` + 자식 `rw-<dsKey>-cN`)으로 발급한다.
 */
function repeatWidgetFor(def: DataSourceDef, allocId: (base: string) => string): RepeatWidget {
  let seq = 0;
  return createRepeatWidgetForDataSource(def, () => {
    seq += 1;
    return allocId(seq === 1 ? `rw-${def.key}` : `rw-${def.key}-c${seq - 1}`);
  });
}

// ============================================================
// ① 미배치 위젯 채우기 (비파괴)
// ============================================================

/** 그룹 서브트리의 본문 필드 키 + 반복 over 수집 — 소속 표 매칭용(repeat children=행 상대키·modalTrigger=모달 스코프는 미하강). */
function collectGroupMatchKeys(group: GroupWidget): { fieldKeys: string[]; overKeys: string[] } {
  const fieldKeys: string[] = [];
  const overKeys: string[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') {
        fieldKeys.push(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type === 'repeat') overKeys.push(row.sourceKey);
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) fieldKeys.push(cell.widget.bindingKey);
        }
      } else if (w.kind === 'repeat') {
        overKeys.push(w.over);
      }
    }
  };
  walk(group.children);
  return { fieldKeys, overKeys };
}

/**
 * 미배치 위젯 채우기 — 현재 트리는 그대로 두고, 미배치 위젯만 **소속 위치**에 삽입한다.
 *
 * 소속 판정: 그 bindingKey가 속한 body 표(scanBody keyToTable — 정본 술어 기준)를 찾고,
 *  - 그 표의 필드/over를 이미 담은 기존 그룹이 있으면(DFS 첫 매칭 = 최외곽 우선) 그 그룹 끝에,
 *  - 없으면 새 그룹 신설(제목 = heading 소비/'표 N' 규칙 — 재구성과 같은 결정 규칙),
 *  - 표 밖/판정 불가(변수·dataDef 등)는 '기타' 그룹(기존 제목 '기타' 그룹 재사용, 없으면 신설).
 * dataSourceRows 풀 위젯은 반복 위젯 + 뼈대로 대체 삽입(배치 채널 강제 단일화 — handleWidgetPlace 동형).
 *
 * 변경 없으면(미배치 0) 원본 참조 반환(no-op — undo 오염 방지).
 */
export function fillUnplacedWidgets(
  template: ReportTemplate,
  pool: ComposerWidget[],
): { next: ReportTemplate; placedCount: number } {
  const tree = template.composerWidgetTree ?? [];
  const { unplaced } = syncComposerWidgets(template.composerWidgetTree, pool);
  if (unplaced.length === 0) return { next: template, placedCount: 0 };

  const { sections, keyToTable, overToTable } = scanBody(template);
  const sectionInfoByTable = new Map(
    sections.map((s, i) => [s.tableId, { title: s.title, index: i }]),
  );

  // 기존 그룹 → 소속 표: 그룹 서브트리의 필드 키/반복 over가 가리키는 표(첫 매칭 그룹 채택).
  // repeat children(행 스코프)·modalTrigger(모달 스코프)는 하강하지 않는다.
  const tableToGroupId = new Map<string, string>();
  const visitGroups = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'group') {
        const { fieldKeys, overKeys } = collectGroupMatchKeys(w);
        for (const k of fieldKeys) {
          const t = keyToTable.get(k);
          if (t && !tableToGroupId.has(t)) tableToGroupId.set(t, w.id);
        }
        for (const k of overKeys) {
          const t = overToTable.get(k);
          if (t && !tableToGroupId.has(t)) tableToGroupId.set(t, w.id);
        }
        visitGroups(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) visitGroups(slot.children);
      }
    }
  };
  visitGroups(tree);

  // 기존 '기타' 그룹 재사용(DFS 첫 등장) — 없으면 필요 시 신설.
  const findEtcGroupId = (nodes: ComposerWidget[]): string | null => {
    for (const w of nodes) {
      if (w.kind === 'group') {
        if (w.title === '기타') return w.id;
        const inner = findEtcGroupId(w.children);
        if (inner) return inner;
      }
    }
    return null;
  };
  const etcGroupId = findEtcGroupId(tree);

  const allocId = makeWidgetIdAllocator(collectWidgetIds(tree));
  const additionsByGroupId = new Map<string, ComposerWidget[]>();
  const newGroupByTable = new Map<string, GroupWidget>();
  let etcNewGroup: GroupWidget | null = null;
  const appendTo = (groupId: string, widget: ComposerWidget): void => {
    const list = additionsByGroupId.get(groupId) ?? [];
    list.push(widget);
    additionsByGroupId.set(groupId, list);
  };

  for (const w of unplaced) {
    const def =
      w.widget === 'dataSourceRows'
        ? (template.dataSources ?? []).find((d) => d.key === w.bindingKey)
        : undefined;
    const toInsert: ComposerWidget = def ? repeatWidgetFor(def, allocId) : w;
    const tableId =
      w.widget === 'dataSourceRows' ? overToTable.get(w.bindingKey) : keyToTable.get(w.bindingKey);
    const existingGroupId = tableId ? tableToGroupId.get(tableId) : undefined;

    if (existingGroupId) {
      appendTo(existingGroupId, toInsert);
    } else if (tableId) {
      let group = newGroupByTable.get(tableId);
      if (!group) {
        group = {
          kind: 'group',
          id: allocId('wg-auto'),
          title: sectionInfoByTable.get(tableId)?.title ?? '기타',
          children: [],
        };
        newGroupByTable.set(tableId, group);
      }
      group.children.push(toInsert);
    } else if (etcGroupId) {
      appendTo(etcGroupId, toInsert);
    } else {
      if (!etcNewGroup) {
        etcNewGroup = { kind: 'group', id: allocId('wg-extra'), title: '기타', children: [] };
      }
      etcNewGroup.children.push(toInsert);
    }
  }

  // 기존 트리에 그룹별 추가분 병합(불변 재구성). repeat/modalTrigger 안은 배치 대상이 아니라 미하강.
  const withAdditions = (nodes: ComposerWidget[]): ComposerWidget[] =>
    nodes.map((w): ComposerWidget => {
      if (w.kind === 'group') {
        const children = withAdditions(w.children);
        const extra = additionsByGroupId.get(w.id);
        return { ...w, children: extra ? [...children, ...extra] : children };
      }
      if (w.kind === 'branch') {
        return {
          ...w,
          branches: w.branches.map((slot) => ({ ...slot, children: withAdditions(slot.children) })),
        };
      }
      return w;
    });

  // 신설 그룹은 body 섹션 순서로 root 말미에, '기타' 신설은 맨 뒤에.
  const orderedNewGroups = [...newGroupByTable.entries()]
    .sort(
      (a, b) =>
        (sectionInfoByTable.get(a[0])?.index ?? 0) - (sectionInfoByTable.get(b[0])?.index ?? 0),
    )
    .map(([, g]) => g);
  const nextTree = [
    ...withAdditions(tree),
    ...orderedNewGroups,
    ...(etcNewGroup ? [etcNewGroup] : []),
  ];
  return {
    next: { ...template, composerWidgetTree: nextTree },
    placedCount: unplaced.length,
  };
}

// ============================================================
// ② 전체 재구성 (파괴 — 호출부가 확인 모달 경유)
// ============================================================

/**
 * body를 걸어 작성 위젯 트리를 새로 생성한다(기존 트리 폐기 — undo로 복구 가능).
 *
 * - 표마다 그룹(제목 = heading 소비/'표 N') + 그 표 파생 필드의 풀 위젯(id `wf-<key>` 규약 그대로 —
 *   위젯 형상 정본은 파생 풀이므로 재구성하지 않고 재사용)을 행→열 읽기 순서로 배치.
 * - 반복영역이 적용된 표: 밴드 셀 필드는 flat에서 제외(rc 정본 술어)되고, 그 repeatRegion의 over
 *   데이터셋을 반복 위젯 + 뼈대 children으로 그룹 자리(밴드 시작 행 순서)에 배치.
 * - **격자 블록은 작성폼 표(formTable)로 미러**(P2) — 그 표의 입력 구간이 직사각 격자면
 *   세로로 풀지 않고 보고서와 같은 표 모양으로 낸다(판정은 detectGridBlocks, 생성은
 *   formTableForBlock). 블록 밖 필드는 같은 그룹 안에서 종전처럼 flat 위젯으로 남고,
 *   격자가 아니면 표 전체가 종전 동작 그대로다.
 *   **P4: 값 열이 하나인 「라벨│값」 세로 격자도 미러한다**(헤더 행이 있을 때만 — detectGridBlocks
 *   게이트 ④). 종전엔 1값열을 통째로 배제했는데, 그래서 실데이터 근로자현황의 `사무직`·`사무직외`
 *   두 입력이 flat으로 풀렸고 **라벨 폴백(`catalog.label → binding.label → bindingKey`)이 body
 *   좌측 텍스트 셀을 보지 않아** 두 위젯 라벨이 완전히 같아졌다(작성자가 구분 불가 — 2026-10-04
 *   dev 실측). 표로 미러하면 그 좌측 셀이 **행 머리말**로 실려 라벨 역할을 한다.
 *   ⚠️ **formTable은 반드시 group «안»에 둔다 — 최상위에 놓지 말 것.**
 *   `fillUnplacedWidgets`의 소속 판정(collectGroupMatchKeys)은 `walk(group.children)`로
 *   시작해 formTable 셀 키를 **그 walk 안에서만** 읽는다. 최상위에 놓으면 그 함수가 표를 보지
 *   못해 **그 표의 셀 키가 어느 body 표에도 귀속되지 않고**, 그래서 나중에 파생 풀이 늘어났을 때
 *   **같은 표의 새 키가 엉뚱한 새 group으로 간다**(소속 오판). 한 표에 블록이 둘이면 group 하나
 *   안에 formTable 둘이다(손저작 양식의 확립된 관례와 동형).
 *   ⚠️ **피해는 「중복 배치」가 아니다 — 그 기제는 팀장이 2026-10-04에 적었다가 되돌림 프로브로
 *   반증됐다.** rc `collectPlacedInputKeys`의 walk는 root부터 돌고 formTable 분기가 그 안에 있어
 *   **최상위 formTable도 placed로 센다** ⇒ `unplaced`는 그대로 비고 삽입은 0이다. 실제로 이 미러를
 *   최상위로 옮겨 돌려 보면 '자기 완결(삽입 0)' 케이스는 **green을 유지**한다.
 *   ⇒ 이 계약의 **행동 탐지기는 spec `[되돌림] formTable은 group 안 — 나중에 늘어난 같은 표의
 *   키가 그 group에 붙는다` 하나뿐**이고 '자기 완결' 케이스는 **이 축의 탐지기가 아니다**.
 *   그 둘을 같은 축으로 읽고 전자를 「중복」이라며 지우지 말 것.
 *   ⚠️ **손저작 formTable과 형상이 다르게 나오는 것이 정상이다.** 실제 양식(안전관리현황보고)의
 *   손저작본은 body를 **전치**해 라벨을 열로 세운 것이고, 전치는 「어느 축이 레코드인가」라는
 *   의미 판단이라 구조→구조 결정적 자동화의 범위 밖이다(결정 2026-07-27). 이 미러는 body의
 *   행·열을 그대로 둔다 — 다르다고 버그로 읽고 전치를 구현하지 말 것.
 * - 어느 그룹에도 못 넣은 잔여 풀(변수·dataDef·표 밖 바인딩·반복영역 없는 데이터셋 등)은 '기타' 그룹.
 *   잔여 dataSourceRows도 repeat으로 대체(배치 채널 단일화).
 */
export function rebuildWidgetTreeFromBody(
  template: ReportTemplate,
  pool: ComposerWidget[],
): { next: ReportTemplate; groupCount: number; widgetCount: number } {
  const { sections } = scanBody(template);
  const poolInputs = pool.filter((w): w is InputWidget => w.kind === 'input');
  const poolByKey = new Map<string, InputWidget>();
  for (const w of poolInputs) {
    if (!poolByKey.has(w.bindingKey)) poolByKey.set(w.bindingKey, w);
  }

  const allocId = makeWidgetIdAllocator(new Set());
  const placed = new Set<string>();
  let widgetCount = 0;
  const nextTree: ComposerWidget[] = [];

  sections.forEach((section, i) => {
    const children: ComposerWidget[] = [];
    // ── 격자 블록 미러(P2) 재료: 엔트리가 이미 들고 있는 (row,col)로 셀 → 키 색인을 만든다 ──
    const keysByCell = new Map<string, string[]>();
    for (const entry of section.entries) {
      if (entry.kind !== 'field') continue;
      const k = `${entry.row}:${entry.col}`;
      const list = keysByCell.get(k) ?? [];
      list.push(entry.key);
      keysByCell.set(k, list);
    }
    // 블록 → formTable(풀 위젯이 없는 블록은 제외 — flat 폴백). keys는 블록 직사각 안의 전 엔트리
    // 키(풀에 없어 셀로 안 들어간 키까지) — 그 키가 flat로 새어 중복 배치되는 것을 막는다.
    const mirrors: { keys: Set<string>; widget: FormTableWidget; placedKeys: string[] }[] = [];
    detectGridBlocks(section.table, keysByCell).forEach((block, bi) => {
      const keys = new Set<string>();
      for (const r of block.dataRows) {
        for (let c = block.colStart; c <= block.colEnd; c += 1) {
          for (const k of keysByCell.get(`${r}:${c}`) ?? []) keys.add(k);
        }
      }
      const made = formTableForBlock(
        block,
        section.table,
        keysByCell,
        poolByKey,
        allocId(`wft-${i}-${bi}`),
      );
      if (made) mirrors.push({ keys, widget: made.widget, placedKeys: made.placedKeys });
    });
    const mirrorIndexByKey = new Map<string, number>();
    mirrors.forEach((m, mi) => m.keys.forEach((k) => mirrorIndexByKey.set(k, mi)));
    const emittedMirrors = new Set<number>();

    for (const entry of section.entries) {
      if (placed.has(entry.key)) continue;
      // 블록 소속 키는 표 위젯이 소비 — 블록 첫 키가 나온 자리(읽기 순서)에 표를 한 번 넣는다.
      const mi = entry.kind === 'field' ? mirrorIndexByKey.get(entry.key) : undefined;
      if (mi !== undefined) {
        if (!emittedMirrors.has(mi)) {
          emittedMirrors.add(mi);
          children.push(mirrors[mi].widget);
          for (const k of mirrors[mi].placedKeys) placed.add(k);
          widgetCount += mirrors[mi].placedKeys.length;
        }
        continue;
      }
      if (entry.kind === 'field') {
        const w = poolByKey.get(entry.key);
        if (!w) continue; // 파생 풀에 없는 키(파생 제외 대상) — 트리에 넣지 않는다
        placed.add(entry.key);
        children.push(w);
        widgetCount += 1;
      } else {
        const def = (template.dataSources ?? []).find((d) => d.key === entry.key);
        // readonly 소스/미선언 over는 작성 위젯이 없다(파생 풀 규약과 동일 판정).
        if (!def || !isEditableListDataSource(def)) continue;
        placed.add(entry.key);
        children.push(repeatWidgetFor(def, allocId));
        widgetCount += 1;
      }
    }
    if (children.length === 0) return;
    nextTree.push({ kind: 'group', id: allocId(`wg-${i}`), title: section.title, children });
  });

  // 잔여 풀 → '기타' 그룹(풀 순서 유지 — 변수가 앞).
  const leftovers: ComposerWidget[] = [];
  for (const w of poolInputs) {
    if (placed.has(w.bindingKey)) continue;
    placed.add(w.bindingKey);
    if (w.widget === 'dataSourceRows') {
      const def = (template.dataSources ?? []).find((d) => d.key === w.bindingKey);
      if (def) {
        leftovers.push(repeatWidgetFor(def, allocId));
        widgetCount += 1;
        continue;
      }
    }
    leftovers.push(w);
    widgetCount += 1;
  }
  if (leftovers.length > 0) {
    nextTree.push({ kind: 'group', id: allocId('wg-extra'), title: '기타', children: leftovers });
  }

  return {
    next: { ...template, composerWidgetTree: nextTree },
    groupCount: nextTree.length,
    widgetCount,
  };
}
